import React from 'react';
import { connect } from '../../common/components/runtime-context';
import hoistNonReactStatics from 'hoist-non-react-statics';
import {
  createHocName,
  EXPERIMENT_ENABLE_PRIVATE_USER_FLOW,
  isMemberAreaActive,
} from '@wix/communities-forum-client-commons';
import { flowRight } from 'lodash';
import {
  getCurrentUser,
  isBlocked,
  isOwner,
  isPrivate,
} from '../../common/store/current-user/current-user-selectors';

import { isSite } from '../../common/store/basic-params/basic-params-selectors';
import withExperiment from './with-experiment';

const NAME = 'withAuth';

export default function withAuth(WrappedComponent) {
  const WithAuth = React.memo(
    ({ __isSite, __isMemberAreaActive, showJoinCommunityPopup, ...props }) => {
      const decorateWithPrivacyChecker = React.useCallback(
        (fn, { preventDefault = false } = {}) => {
          const isPrivateUserFlowEnabled = props.isPrivateUserFlowEnabled;
          const isPrivate = () => props.isPrivate;
          const isAuthenticated = () => props.isAuthenticated;

          return function (...args) {
            if (preventDefault) {
              const event = args[0];
              event.preventDefault && event.preventDefault();
            }

            if (isPrivateUserFlowEnabled && isAuthenticated() && isPrivate()) {
              showJoinCommunityPopup();
            } else {
              return fn.apply(this, args);
            }
          };
        },
        [
          props.isPrivate,
          props.isAuthenticated,
          props.isPrivateUserFlowEnabled,
          showJoinCommunityPopup,
        ],
      );

      const forPublicUser = React.useCallback(
        (fn, options) => {
          if (!__isSite) {
            return fn;
          }
          return decorateWithPrivacyChecker(fn, options);
        },
        [__isSite, decorateWithPrivacyChecker],
      );

      return <WrappedComponent forPublicUser={forPublicUser} {...props} />;
    },
  );

  WithAuth.displayName = createHocName(NAME, WrappedComponent);
  hoistNonReactStatics(WithAuth, WrappedComponent);

  return flowRight(
    withExperiment({
      isPrivateUserFlowEnabled: EXPERIMENT_ENABLE_PRIVATE_USER_FLOW,
    }),
    connect(mapRuntimeToProps),
  )(WithAuth);
}

function mapRuntimeToProps(state, ownProps, actions) {
  return {
    showJoinCommunityPopup: actions.showJoinCommunityPopup,
    currentUser: getCurrentUser(state),
    isAuthenticated: Boolean(state.currentUser),
    isBlocked: isBlocked(state),
    isOwner: isOwner(state),
    isPrivate: isPrivate(state),
    __isMemberAreaActive: isMemberAreaActive(state),
    __isSite: isSite(state),
  };
}
